import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Card, CardBody, Col, Container, Label, Row, Spinner } from "reactstrap";
import { AvField, AvForm } from "availity-reactstrap-validation";
import { userActions } from "../../store/auth/login/userSlice";
import { dataActions } from "../../modules/dataSlice";
import logo from "../../assets/images/ja-eval_logo_horizontal_transparent.png";
import authBg from "../../assets/images/authentication-bg.jpg";
import "./login-modern.css";

function Login() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { loading, error } = useSelector(state => state.user);
    const { isFetching } = useSelector(state => state.data);
    const isBusy = loading || isFetching;
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(() => localStorage.getItem("rememberedUsername") !== null);
    const [showPasswordHelp, setShowPasswordHelp] = useState(false);

    useEffect(() => {
        document.body.classList.add("auth-body-bg");
        return () => {
            document.body.classList.remove("auth-body-bg");
        };
    }, []);

    const formik = useFormik({
        initialValues: {
            username: localStorage.getItem("rememberedUsername") || "",
            password: "",
        },
        onSubmit: (values) => {
            if (rememberMe) {
                localStorage.setItem("rememberedUsername", values.username);
            } else {
                localStorage.removeItem("rememberedUsername");
            }

            dispatch(userActions.login({
                user: values,
                history: navigate,
                onSuccess: () => {
                    setTimeout(() => {
                        dispatch(dataActions.loadData());
                    }, 2000);
                },
            }));
        },
    });

    return (
        <React.Fragment>
            {isBusy && (
                <div id="preloader">
                    <div id="status">
                        <div className="spinner">
                            <i className="ri-loader-line spin-icon"></i>
                        </div>
                    </div>
                </div>
            )}

            <div className="modern-login-page">
                <Container fluid className="h-100 p-0">
                    <Row className="g-0 min-vh-100">
                        <Col lg={7} className="login-brand-panel d-none d-lg-flex">
                            <div className="login-visual" style={{ backgroundImage: `url(${authBg})` }}></div>
                            <div className="brand-content">
                                <div className="brand-badge">PLATEFORME DE GESTION DES ÉPAVES</div>
                                <h1>Optimisez la gestion de vos épaves et maximisez vos résultats.</h1>
                                <p>
                                    Centralisez et pilotez vos dossiers de réforme, suivez les valeurs vénales,
                                    gérez les offres de maximisation, vos épavistes et vos lots multi-véhicules
                                    dans un espace sécurisé, structuré et performant.
                                </p>

                                <div className="brand-stats">
                                    <div>
                                        <strong>Dossiers de réforme</strong>
                                        <span>Gérez vos dossiers d’épaves de leur création jusqu’à leur clôture, avec un suivi complet des statuts, documents et historiques.</span>
                                    </div>
                                    <div>
                                        <strong>Valeur vénale et maximisation</strong>
                                        <span>Estimez les valeurs vénales, centralisez les offres reçues et suivez les résultats de maximisation pour chaque dossier.</span>
                                    </div>
                                    <div>
                                        <strong>Gestion des épavistes</strong>
                                        <span>Administrez votre réseau d’épavistes, suivez leurs offres, leurs engagements et leurs performances.</span>
                                    </div>
                                    <div>
                                        <strong>Lots multi-véhicules</strong>
                                        <span>Créez, organisez, diffusez et attribuez des lots de véhicules afin d’optimiser leur valorisation et leur vente.</span>
                                    </div>
                                </div>

                                <div className="brand-benefits" aria-label="Avantages de la plateforme">
                                    <span>Données centralisées</span>
                                    <span>Traçabilité complète</span>
                                    <span>Gestion sécurisée</span>
                                    <span>Optimisation des résultats</span>
                                </div>
                            </div>
                        </Col>

                        <Col lg={5} className="login-form-panel">
                            <div className="login-card-wrapper">
                                <Card className="modern-login-card">
                                    <CardBody>
                                        <div className="login-header">
                                            <img src={logo} alt="JA-Eval" className="login-logo" />
                                            <span className="login-kicker">ESPACE ENTREPRISE</span>
                                            <h2>Bienvenue</h2>
                                            <p>Connectez-vous à la plateforme JA-Eval</p>
                                        </div>

                                        {error && <Alert color="danger" className="modern-alert">{error}</Alert>}

                                        <AvForm className="form-horizontal login-form-content" onValidSubmit={formik.handleSubmit}>
                                            <div className="modern-field">
                                                <Label htmlFor="username">Identifiant</Label>
                                                <div className="field-wrapper">
                                                    <i className="ri-user-3-line"></i>
                                                    <AvField
                                                        {...formik.getFieldProps("username")}
                                                        type="text"
                                                        id="username"
                                                        placeholder="Saisissez votre identifiant"
                                                        autoComplete="username"
                                                        validate={{ required: { value: true } }}
                                                        disabled={isBusy}
                                                    />
                                                </div>
                                            </div>

                                            <div className="modern-field">
                                                <Label htmlFor="userpassword">Mot de passe</Label>
                                                <div className="field-wrapper">
                                                    <i className="ri-lock-password-line"></i>
                                                    <AvField
                                                        {...formik.getFieldProps("password")}
                                                        type={showPassword ? "text" : "password"}
                                                        id="userpassword"
                                                        placeholder="Saisissez votre mot de passe"
                                                        autoComplete="current-password"
                                                        validate={{ required: { value: true } }}
                                                        disabled={isBusy}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="password-visibility-toggle"
                                                        onClick={() => setShowPassword(value => !value)}
                                                        aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                                                        title={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                                                    >
                                                        <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="login-options">
                                                <label className="remember-option" htmlFor="remember-me">
                                                    <input
                                                        id="remember-me"
                                                        type="checkbox"
                                                        checked={rememberMe}
                                                        onChange={event => setRememberMe(event.target.checked)}
                                                    />
                                                    <span>Se souvenir de moi</span>
                                                </label>
                                                <button type="button" className="forgot-password-link" onClick={() => setShowPasswordHelp(value => !value)}>
                                                    Mot de passe oublié ?
                                                </button>
                                            </div>

                                            {showPasswordHelp && (
                                                <div className="password-help" role="status">
                                                    Contactez l’administrateur de votre entreprise pour réinitialiser votre mot de passe.
                                                </div>
                                            )}

                                            <Button className="modern-login-btn" type="submit" disabled={isBusy}>
                                                {isBusy ? (
                                                    <React.Fragment>
                                                        <Spinner size="sm" className="me-2" />
                                                        Connexion...
                                                    </React.Fragment>
                                                ) : (
                                                    <React.Fragment>
                                                        Se connecter
                                                        <i className="ri-arrow-right-line ms-2"></i>
                                                    </React.Fragment>
                                                )}
                                            </Button>
                                        </AvForm>

                                        <div className="login-security-note">
                                            <i className="ri-shield-check-line"></i>
                                            <span>Vos données sont sécurisées et confidentielles.</span>
                                        </div>

                                        <div className="login-footer">
                                            <p>Développé par <a href="https://ideracloud.com" target="_blank" rel="noreferrer">IDERACLOUD</a> en partenariat avec YALILA GROUP</p>
                                        </div>
                                    </CardBody>
                                </Card>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
}

export default Login;
